import { useState, useCallback, MutableRefObject } from 'react';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { PhoneSyncApi, queryDevices, queryKeys } from '@frontend/api-phone-sync';
import { useAppSettingsShallowStore } from '@frontend/app-settings-store';
import { useTranslation } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import {
  DeviceSelectModal,
  LosingAccessModal,
  PhoneSyncButton,
  PhoneSyncModal,
  usePhonePairSuccessHandler,
  usePhoneSyncControl,
  usePhoneSyncMutations,
} from '@frontend/phone-sync';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { SoftphoneIsConnectedTooltip, SoftphoneCallActiveError, usePhoneConfigInfo } from '../shared';

export const ERRORS = {
  SOFTPHONE_CALL_ACTIVE: 'softphone call active',
  OPENING_DEVICE_SELECT_MODAL: 'opening device select modal',
};

type UseUnprivilegedPhoneSoftphoneIntegrationProps = {
  handleLocationChange: (params: { locationIds: string[]; orgId: string }) => void;
  closeModal?: () => void;
  futureLocationIds: MutableRefObject<string[]>;
  futureOrgId: MutableRefObject<string>;
};

export const useUnprivilegedPhoneSoftphoneIntegration = ({
  handleLocationChange,
  closeModal,
  futureLocationIds,
  futureOrgId,
}: UseUnprivilegedPhoneSoftphoneIntegrationProps) => {
  const { selectedLocationIds, accessibleLocationData: accessibleScopeData, accessibleGroupData } = useAppScopeStore();
  const { t } = useTranslation('location-picker');
  const deviceSelectModal = useModalControl();
  const losingAccessModal = useModalControl();

  // Phone related state and hooks
  const { setPhoneConfig, phoneConfig } = usePhoneConfigShallowStore('phoneConfig', 'setPhoneConfig');
  const { disconnectDevice } = usePhoneSyncMutations();
  const { modalProps, buttonProps } = usePhoneSyncControl();
  const onSyncSuccess = usePhonePairSuccessHandler()(selectedLocationIds);

  // Softphone related state
  const [showActivePhoneCallError, setShowActivePhoneCallError] = useState(false);
  const { hasActiveSoftphoneCalls } = useAppSettingsShallowStore('hasActiveSoftphoneCalls');
  const [hasFutureAvailableDevices, setHasFutureAvailableDevices] = useState(false);

  const phoneConfigInfo = usePhoneConfigInfo({
    phoneConfig,
    accessibleLocationData: { ...accessibleScopeData, ...accessibleGroupData },
  });

  // Get whether or not we can update the location filters when a softphone call is active.
  const getCanUpdateWhenSoftphoneActive = useCallback(
    ({ orgId, locationIds }: { orgId: string; locationIds: string[] }) => {
      if (!hasActiveSoftphoneCalls || !phoneConfigInfo?.isSoftphone) return true;

      const softphoneOrgIdInFlight = orgId === phoneConfigInfo.orgId;
      const softphoneLocationIdInFlight = locationIds.includes(phoneConfigInfo.locationId);

      // We can update if the orgId & locationId associated w/ the softphone are 'in flight'
      return softphoneOrgIdInFlight && softphoneLocationIdInFlight;
    },
    [hasActiveSoftphoneCalls, phoneConfigInfo]
  );

  const queryClient = useQueryClient();

  const onClickApply = useCallback(
    async ({ locationIds, orgId }: { locationIds: string[]; orgId: string }) => {
      if (!getCanUpdateWhenSoftphoneActive({ orgId, locationIds })) {
        setShowActivePhoneCallError(true);
        return Promise.reject(ERRORS.SOFTPHONE_CALL_ACTIVE);
      }

      // Get the new list of devices that are available within the selected location ids
      const devices = await queryClient.fetchQuery({
        queryKey: [locationIds, ...queryKeys.listDevices()],
        queryFn: () => queryDevices(locationIds),
      });

      // These are devices that are currently in context (available within the selected location ids)
      const availableDevices = devices?.filter((device) => device.availableInContext);
      const isCurrentDeviceWithinContext =
        phoneConfig && availableDevices?.some((device) => phoneConfig.sipProfileId === device.sipProfileId);

      const movingAwayFromCurrentConfig = phoneConfig && !isCurrentDeviceWithinContext;

      const hasAvailableDevices = availableDevices ? availableDevices.length > 0 : false;
      setHasFutureAvailableDevices(hasAvailableDevices);

      // The SelectDeviceModal should open if the location with the connected device is deselected (or if no device is connected),
      // and the newly selected location has available devices to be connected.
      if (movingAwayFromCurrentConfig) {
        futureLocationIds.current = locationIds;
        futureOrgId.current = orgId;
        if (hasAvailableDevices) {
          deviceSelectModal.openModal();
        } else {
          // No available devices to connect to - warn the user
          losingAccessModal.openModal();
        }
        return Promise.reject(ERRORS.OPENING_DEVICE_SELECT_MODAL);
      }

      handleLocationChange({ locationIds, orgId });
      return Promise.resolve();
    },
    [
      getCanUpdateWhenSoftphoneActive,
      phoneConfig,
      queryClient,
      deviceSelectModal,
      losingAccessModal,
      handleLocationChange,
      futureLocationIds,
      futureOrgId,
    ]
  );

  const onApplyError = useCallback((error: unknown) => {
    if (error === ERRORS.SOFTPHONE_CALL_ACTIVE) {
      return;
    }

    if (error === ERRORS.OPENING_DEVICE_SELECT_MODAL) {
      return;
    }

    console.error(error);
  }, []);

  const onClickOrgRadio = useCallback(() => {
    setShowActivePhoneCallError(false);
  }, []);

  const renderOrgInfo = useCallback(
    (orgId: string) => {
      if (phoneConfigInfo?.orgId === orgId) {
        return <SoftphoneIsConnectedTooltip deviceName={phoneConfig?.name} />;
      }

      return null;
    },
    [phoneConfigInfo, phoneConfig]
  );

  const renderLocationInfo = useCallback(
    (locationId: string) => {
      if (phoneConfigInfo?.locationId === locationId) {
        return <SoftphoneIsConnectedTooltip deviceName={phoneConfig?.name} />;
      }

      return null;
    },
    [phoneConfigInfo, phoneConfig]
  );

  const renderBannerInfo = useCallback(() => {
    if (showActivePhoneCallError && phoneConfigInfo) {
      return (
        <div
          css={css`
            padding: ${theme.spacing(2, 2, 1, 2)};
            border-top: 1px solid ${theme.colors.neutral20};
          `}
        >
          <SoftphoneCallActiveError locationName={accessibleScopeData[phoneConfigInfo.locationId]?.name || ''} />
        </div>
      );
    }

    return null;
  }, [showActivePhoneCallError, phoneConfigInfo, accessibleScopeData]);

  const renderPhoneRelatedModals = useCallback(() => {
    return (
      <>
        {hasFutureAvailableDevices ? (
          <>
            <DeviceSelectModal
              variant='location-picker'
              locationIds={futureLocationIds.current}
              deviceSelectModal={deviceSelectModal}
              closeModal={() => {
                handleLocationChange({ orgId: futureOrgId.current, locationIds: futureLocationIds.current });
                closeModal?.();
              }}
              pairDeviceButton={
                <PhoneSyncButton
                  {...buttonProps}
                  variant='tertiary'
                  onClick={() => {
                    deviceSelectModal.closeModal();
                  }}
                />
              }
            />
            <PhoneSyncModal
              {...modalProps}
              locationIds={futureLocationIds.current}
              onBack={() => {
                deviceSelectModal.openModal();
                modalProps.onClose();
              }}
              onSyncSuccess={(config) => {
                onSyncSuccess(config);
                handleLocationChange({ orgId: futureOrgId.current, locationIds: futureLocationIds.current });
                closeModal?.();
              }}
              syncFn={PhoneSyncApi.pairDevice}
            />
          </>
        ) : (
          <>
            <LosingAccessModal
              modal={losingAccessModal}
              onContinue={() => {
                disconnectDevice();
                setPhoneConfig(undefined);
                handleLocationChange({ orgId: futureOrgId.current, locationIds: futureLocationIds.current });
                modalProps.onClose();
                closeModal?.();
              }}
              pairDeviceButton={
                <PhoneSyncButton
                  {...buttonProps}
                  variant='secondary'
                  btnLabel={t('Pair New Device')}
                  onClick={() => {
                    losingAccessModal.closeModal();
                  }}
                />
              }
            />
            <PhoneSyncModal
              {...modalProps}
              locationIds={futureLocationIds.current}
              onBack={() => {
                modalProps.onClose();
              }}
              onSyncSuccess={(config) => {
                onSyncSuccess(config);
                handleLocationChange({ orgId: futureOrgId.current, locationIds: futureLocationIds.current });
                closeModal?.();
              }}
              syncFn={PhoneSyncApi.pairDevice}
            />
          </>
        )}
      </>
    );
  }, [
    hasFutureAvailableDevices,
    futureLocationIds,
    deviceSelectModal,
    buttonProps,
    modalProps,
    losingAccessModal,
    t,
    handleLocationChange,
    futureOrgId,
    closeModal,
    onSyncSuccess,
    disconnectDevice,
    setPhoneConfig,
  ]);

  return {
    onClickApply,
    onApplyError,
    onClickOrgRadio,
    renderOrgInfo,
    renderLocationInfo,
    renderBannerInfo,
    renderPhoneRelatedModals,
  };
};
